import type { DemoState } from '../state';
import {
  recordRecoverSourceStateFallback,
  type RecoverSourceStateFallbackContext,
} from './telemetry';

let callCount = 0;
let telemetry: ((info: { reason: string; ts: number }) => void) | null = null;

export function setRecoverSourceStateTelemetry(fn: typeof telemetry): void {
  telemetry = fn;
}

export function getRecoverSourceStateCallCount(): number {
  return callCount;
}

export function resetRecoverSourceStateForTest(): void {
  callCount = 0;
  telemetry = null;
}

export interface RecoverSourceResult {
  recovered: DemoState;
  fired: boolean;
  reason: string | null;
}

/**
 * Fallback path, not normal operation. This firing in production is a bug
 * signal that `state.source` was released while `state.sourceOriginal` still
 * existed and an export/render path needed to recover it.
 */
export function recoverSourceState(
  state: DemoState,
  buildAdjustedSource: (s: DemoState) => ImageData,
): RecoverSourceResult {
  if (state.source !== null || state.sourceOriginal === null) {
    return { recovered: state, fired: false, reason: null };
  }

  const reason = 'source-null-with-original-present';
  callCount++;
  const context: RecoverSourceStateFallbackContext = {
    reason,
    missingField: 'source',
    hasSource: state.source !== null,
    hasSourceOriginal: state.sourceOriginal !== null,
    regionCount: state.regions.length,
    regionEntryCount: state.regionEntries.length,
    activeRegionCount: state.activeRegionUuids.length,
    assetCount: state.assetLibrary.length,
    hasLabelMap: state.labelMap !== null,
    sourceOriginalWidth: state.sourceOriginal.width,
    sourceOriginalHeight: state.sourceOriginal.height,
    ts: Date.now(),
  };
  if (telemetry) telemetry({ reason, ts: context.ts });
  recordRecoverSourceStateFallback(context);
  console.warn(
    '[amitone] recoverSourceState fallback fired:',
    context,
  );
  const recoveredSource = buildAdjustedSource(state);
  return { recovered: { ...state, source: recoveredSource }, fired: true, reason };
}

export const recoverSourceFromOriginal = recoverSourceState;
